import { useIncomeTypes } from "@/stores/incomeTypes";
import { incomeOptions, incomeLabel } from "@/utils/incomeTypes";
import { Button, Input, message, Modal, Tag, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import PageTab from "@/components/i-pageTab/i-pageTab";
import PageTable from "@/components/i-pageTable/i-pageTable";

import {
  setIcons,
  downloadFile,
  getTimeFormat,
  getFirstDay,
} from "@/utils/common";
import {
  merOrderStatisticsList,
  transferOrderStatisticsExport,
  editTransferOrder,
} from "@/api/statistics";
import { getConfigValueByKey } from "@/api/setting";

const TransferOrderManager = () => {
  const incomeTypes = useIncomeTypes();
  const amountTypeOptions = incomeOptions(incomeTypes, "transfer");
  const [pageData, setPageData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [remarkModalOpen, setRemarkModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [editRemark, setEditRemark] = useState("");
  const [remarkSubmitLoading, setRemarkSubmitLoading] = useState(false);

  const transferTypeList = [
    { label: "管理员转赠", value: 0 },
    { label: "团队内转赠", value: 1 },
  ];

  const [receivedTypeOptions, setReceivedTypeOptions] = useState<{ label: string; value: number }[]>([]);
  
  const [formData, setFormData] =
    useState<SelectCmsMerOrderStatisticsPageParams>({
      startTime: getTimeFormat(getFirstDay(new Date()), "yyyy-MM-dd HH:mm:ss"),
      endTime: getTimeFormat(new Date(), "yyyy-MM-dd HH:mm:ss"),
      mobile: "",
      transferMobile: "",
      transferType: '',
      transferAmountType: undefined as number | undefined,
      ypageNum: 1,
      ypageSize: 10,
    });

  const formTab = [
    {
      label: "赠送人账号",
      name: "mobile",
      type: "input",
      width: "240px", 
    },
    {
      label: "接收人账号",
      name: "transferMobile",
      type: "input",
      width: "240px",
    },
    {
      label: "转赠方式",
      name: "transferType",
      type: "select",
      options: transferTypeList,
    },
    {
      label: "转赠类型",
      name: "transferAmountType",
      type: "select",
      options: amountTypeOptions,
      placeholder: "请选择转赠类型",
    },
    {
      label: "收款类型",
      name: "receivedType",
      type: "select",
      options: receivedTypeOptions,
      placeholder: "请选择转赠类型",
    },
    {
      label: "创建时间",
      name: "rangePicker",
      type: "rangePicker",
      // labelCol: { span:4, offset: 0 },
      width: "380px",
    },
  ];


  const columns = [
    {
      title: "转赠订单号",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "赠送人ID",
      dataIndex: "userId",
      key: "userId",
    },
    {
      title: "赠送人昵称",
      dataIndex: "nickName",
      key: "nickName",
    },
    {
      title: "赠送人账号",
      dataIndex: "mobile",
      key: "mobile",
    },
    {
      title: "接收人ID",
      dataIndex: "transferUserId",
      key: "transferUserId",
    },
    {
      title: "接收人昵称",
      dataIndex: "transferNickName",
      key: "transferNickName",
    },
    {
      title: "接收人账号",
      dataIndex: "transferMobile",
      key: "transferMobile",
    },
  
    {
      title: "转赠金额",
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: "手续费率",
      dataIndex: "serviceCharge",
      key: "serviceCharge",
    },
    {
      title: "转赠方式",
      dataIndex: "transferType",
      key: "transferType",
      render: (text: number) => transferTypeList.find(item => item.value === text)?.label,
    },
    {
      title: "转赠类型",
      dataIndex: "amountTypeStr",
      key: "amountTypeStr",
      render: (text: string, row: { transferAmountType?: number }) => <Tag color="blue">{incomeLabel(amountTypeOptions, row.transferAmountType ?? 0, text)}</Tag>,
    },
    {
      title: "收款类型",
      dataIndex: "receivedTypeStr",
      key: "receivedTypeStr",
      render: (text: string) => text && <Tag color="blue">{text}</Tag>,
    },
    {
      title: "创建时间",
      dataIndex: "createTime",
      key: "createTime",
    },
    {
      title: "备注",
      dataIndex: "remark",
      key: "remark",
      width: "180px",
      render: (text: string) => <Tooltip title={text}> <div style={{ width: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{text}</div></Tooltip>,
  
    },
    {
      title: "操作",
      dataIndex: "action",
      key: "action",
      render: (text: string, record: any) => <Button type="link" onClick={() => handleEditRemark(record)}>编辑备注</Button>,
    },
  ];

  const footer = [
    {
      title: "导出",
      icon: setIcons("DownloadOutlined"),
      onClick: () => exportExcel(),
      loading: exportLoading,
    },
  ];

  useEffect(() => {
    getPageData();
  }, [formData]);

  useEffect(() => {
    (async () => {
      try {
        const receivedConfig = await getConfigValueByKey("transfer.received.type");
        if (!receivedConfig) {
          setReceivedTypeOptions([]);
          return;
        }
        const receivedList = JSON.parse(receivedConfig) as { receivedType: number; typeName: string }[];
        if (Array.isArray(receivedList)) {
          setReceivedTypeOptions(receivedList.map((item) => ({ label: item.typeName, value: item.receivedType })));
        }
      } catch {
        setReceivedTypeOptions([]);
      }
    })();
  }, []);

  const handleEditRemark = (record: any) => {
    setEditingRecord(record);
    setEditRemark(record.remark ?? "");
    setRemarkModalOpen(true);
  };

  const handleRemarkModalOk = async () => {
    if (!editingRecord?.id) return;
    setRemarkSubmitLoading(true);
    try {
      await editTransferOrder({ id: editingRecord.id, remark: editRemark });
      message.success("备注修改成功");
      setRemarkModalOpen(false);
      setEditingRecord(null);
      setEditRemark("");
      getPageData();
    } catch {
    } finally {
      setRemarkSubmitLoading(false);
    }
  };

  const handleRemarkModalCancel = () => {
    setRemarkModalOpen(false);
    setEditingRecord(null);
    setEditRemark("");
  };

  const getPageData = async () => {
    setLoading(true);
    try {
      const data = await merOrderStatisticsList(formData);
      setPageData(data.rows);
      setTotal(data.total);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    setExportLoading(true);
    try {
      const data = await transferOrderStatisticsExport(formData);
      downloadFile(
        data as any,
        "转赠订单导出" + getTimeFormat(new Date(), "yyyy-MM-dd HH:mm:ss")
      );
    } catch (error) {
    } finally {
      setExportLoading(false);
    }
  };
  const search = (val: any) => {
    setFormData({
      ...formData,
      ...val,
      ypageNum: 1,
    });
  };

  const reset = () => {
    setFormData({
      startTime: getTimeFormat(getFirstDay(new Date()), "yyyy-MM-dd HH:mm:ss"),
      endTime: getTimeFormat(new Date(), "yyyy-MM-dd HH:mm:ss"),
      mobile: "",
      transferMobile: "",
      transferType: "",
      transferAmountType: undefined,
      ypageNum: 1,
      ypageSize: 10,
    });
  };

  const pageChange = (current: number, pageSize: number) => {
    setFormData({
      ...formData,
      ypageNum: current,
      ypageSize: pageSize,
    });
  };
  return (
    <div>
      <PageTab
        search={search}
        reset={reset}
        formTab={formTab}
        footer={footer}
      ></PageTab>
      <PageTable
        loading={loading}
        pageChange={pageChange}
        rowKey="id"
        columns={columns}
        data={pageData}
        pagination={{
          current: formData.ypageNum!,
          pageSize: formData.ypageSize!,
          total: total,
        }}
      ></PageTable>
      <Modal
        title="编辑备注"
        open={remarkModalOpen}
        onOk={handleRemarkModalOk}
        onCancel={handleRemarkModalCancel}
        confirmLoading={remarkSubmitLoading}
        destroyOnClose
        width={480}
      >
        <Input.TextArea
          value={editRemark}
          onChange={(e) => setEditRemark(e.target.value)}
          placeholder="请输入备注"
          rows={4}
          style={{ marginTop: 16 }}
        />
      </Modal>
    </div>
  );
};

export default TransferOrderManager;
